/**
 * Onboarding step 2: the health intake, one question at a time (INT-001 to INT-006). Answers save
 * as they are given. Closing the tab and coming back resumes at the first unanswered question.
 * A finished intake opens a recap, not the first question again.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useRef, useState } from "react";
import type { IntakeAnswers, QuestionId } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { focusRing } from "@lattice/ui/focus";
import { ErrorState } from "@lattice/ui/ErrorState";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import { loadCurrentFormula, loadIntakeOutcome, requestDraft, saveIntakeAnswer } from "../api.ts";
import { navigate } from "../router.ts";
import { QuestionBody } from "./QuestionBody.tsx";
import { answerLine, intakeComplete, PROMPT, visibleQuestions } from "./questions.ts";
import { Shell, stepText } from "./Shell.tsx";
import { WhatHappens } from "./WhatHappens.tsx";
function withAnswer(answers: IntakeAnswers, id: QuestionId, answer: NonNullable<IntakeAnswers[QuestionId]>): IntakeAnswers {
  return {
    ...answers,
    [id]: answer
  };
}
function stepAfter(answers: IntakeAnswers, id: QuestionId): number | "done" {
  const vis = visibleQuestions(answers);
  const at = vis.indexOf(id);
  if (at === -1 || at + 1 >= vis.length) return "done";
  return at + 1;
}
const _temp = {
  kzqmXN: "x5lhr3w",
  "$$css": true
};
const styles = {
  fill: {
    kZKoxP: "x5yr21d",
    kWkggS: "xmbr0q7",
    k1ekBW: "xxrbq2n",
    kIyJzY: "x149xavy",
    kAMwcw: "xa02eh3",
    $$css: true
  },
  width: (fraction: number) => [_temp, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${Math.round(fraction * 100)}%`)
  }]
};
function Recap({
  answers,
  sent,
  moved,
  onChange
}: {
  readonly answers: IntakeAnswers;
  readonly sent: boolean;
  readonly moved: {
    current: boolean;
  };
  readonly onChange: (id: QuestionId) => void;
}) {
  const vis = visibleQuestions(answers);
  return <Shell title="Your answers" step={2} of={3} aside={<WhatHappens />}>
      <div {...stylex.props(stepText.block)}>
        <MovedHeading moved={moved} describedBy="recap-lead">
          Your answers
        </MovedHeading>
        <p id="recap-lead" {...stylex.props(stepText.lead)}>
          {sent ? "A physician already has your formula. You can still change an answer. It stays in your record and does not replace that review." : "Check these, then send them to a physician."}
        </p>
      </div>
      <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf" data-testid="answer-recap">
        {vis.map(id => <li key={id} className="x78zum5 xdt5ytf xskm3c9 xp1t01x x1t74ka8 x1q0q8m5 xso031l">
            <div className="x78zum5 x6s0dn4 x1qughib x626il7">
              <p className="x1ghz6dp xl4dl4e x1jty25b">{PROMPT[id].heading}</p>
              <Button tone="quiet" size="small" onClick={() => onChange(id)}>
                Change
              </Button>
            </div>
            <p className="x1ghz6dp x1fzhlzt">{answerLine(id, answers)}</p>
          </li>)}
      </ul>
      <div {...stylex.props(stepText.actions)}>
        {sent ? <Button testId="see-formula" wide onClick={() => navigate("/start/reviewing")}>
            See your formula
          </Button> : <Button testId="to-physician" wide onClick={() => {
        void requestDraft();
        navigate("/start/reviewing");
      }}>
            Send to a physician
          </Button>}
      </div>
    </Shell>;
}

/**
 * A heading that takes focus when the visitor moved here, from a question to another or between a
 * question and the recap. The control that was pressed has changed or gone, so focus would stay
 * behind and a screen reader would say nothing. `moved` is consumed on mount, so only the first
 * heading after a move takes focus, and a heading shown again for another reason does not.
 */
function MovedHeading({
  moved,
  id,
  describedBy,
  children
}: {
  readonly moved: {
    current: boolean;
  };
  readonly id?: string;
  readonly describedBy?: string;
  readonly children: string;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    heading.current?.focus();
  }, [moved]);
  return <h1 ref={heading} id={id} tabIndex={-1} aria-describedby={describedBy} {...stylex.props(stepText.heading, focusRing)}>
      {children}
    </h1>;
}
export function Questions() {
  const [answers, setAnswers] = useState<IntakeAnswers | null>(null);
  const [index, setIndex] = useState(0);
  const [editing, setEditing] = useState(false);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const startIndex = useRef(0);
  // The recap and an edit from it are not a walk through the questions, so the browser's back button leaves them alone.
  const stepping = useRef(false);
  /* Set when the visitor moves to another question, so the new heading takes focus. The first
     question on load does not, or the page would open with focus somewhere nobody put it. */
  const moved = useRef(false);
  useEffect(() => {
    void Promise.all([loadIntakeOutcome(), loadCurrentFormula()]).then(([loaded, formula]) => {
      if (loaded === "failed") {
        setLoadFailed(true);
        return;
      }
      if (!loaded) {
        navigate("/start");
        return;
      }
      const vis = visibleQuestions(loaded);
      const firstOpen = vis.findIndex(id => loaded[id] === undefined);
      setAnswers(loaded);
      setSent(formula !== null);
      startIndex.current = firstOpen === -1 ? 0 : firstOpen;
      setIndex(startIndex.current);
      setEditing(firstOpen !== -1);
    }, () => setLoadFailed(true));
  }, []);

  /*
    Each forward step is a history entry carrying its question, so the browser's back button steps
    to the previous question instead of leaving the questions.
  */
  useEffect(() => {
    // Entries this screen pushes hold their question number as the state; the opening entry holds null.
    const onPop = (event: PopStateEvent) => {
      if (!stepping.current) return;
      moved.current = true;
      setIndex(event.state === null ? startIndex.current : Number(event.state));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  if (!answers && loadFailed) return <Shell title="Your answers" step={2} of={3} aside={<WhatHappens />}>
        <ErrorState variant="page" focusOnShow title="We couldn't load your answers" body="Try again in a minute. If it keeps happening, write to support." onRetry={() => window.location.reload()} />
      </Shell>;
  if (!answers) return <Shell title="Your answers" step={2} of={3} aside={<WhatHappens />}>
        <Pending message="Loading your answers" bones={["title", "line", "control", "control"]} />
      </Shell>;
  const vis = visibleQuestions(answers);
  stepping.current = !intakeComplete(answers);
  const current = vis[index] ?? vis[0];
  if (intakeComplete(answers) && !editing) return <Recap answers={answers} sent={sent} moved={moved} onChange={id => {
    const at = vis.indexOf(id);
    moved.current = true;
    setIndex(at === -1 ? 0 : at);
    setEditing(true);
  }} />;
  if (!current) return <Shell title="Your answers" step={2} of={3} aside={<WhatHappens />}>
        <Pending message="Sending your answers to a physician" bones={["title", "line"]} />
      </Shell>;
  const prompt = PROMPT[current];
  const fraction = vis.length === 0 ? 0 : index / vis.length;
  const alreadyComplete = intakeComplete(answers);
  const commit = async (id: QuestionId, answer: NonNullable<IntakeAnswers[QuestionId]>) => {
    if (saving) return;
    setSaving(true);
    setFailed(false);
    const ok = await saveIntakeAnswer(id, answer);
    if (!ok) {
      setFailed(true);
      setSaving(false);
      return;
    }
    const next = withAnswer(answers, id, answer);
    setAnswers(next);
    setSaving(false);
    if (alreadyComplete) {
      moved.current = true;
      setEditing(false);
      return;
    }
    const step = stepAfter(next, id);
    if (step === "done") {
      void requestDraft();
      navigate("/start/reviewing");
      return;
    }
    window.history.pushState(step, "");
    moved.current = true;
    setIndex(step);
  };
  return <Shell title={prompt.heading} step={2} of={3} aside={<WhatHappens />}>
      <div className="xnp4br5 x1m3fsnl x1890um5 xb3r6kr" role="progressbar" aria-label="Intake progress" aria-valuenow={Math.round(fraction * 100)}>
        <div {...stylex.props(styles.fill, styles.width(fraction))} />
      </div>
      <div {...stylex.props(stepText.block)}>
        <p {...stylex.props(stepText.lead)}>
          Question {String(index + 1)} of {String(vis.length)}
        </p>
        <MovedHeading key={current} moved={moved} id="question" describedBy="question-lead">
          {prompt.heading}
        </MovedHeading>
        <p id="question-lead" {...stylex.props(stepText.lead)}>
          {prompt.lead}
        </p>
      </div>
      {saving && <Status>Saving your answer</Status>}
      {failed && <Status state="problem">We couldn't save that. Check your connection and try again.</Status>}
      <QuestionBody id={current} labelledBy="question" answers={answers} onAnswer={(id, answer) => void commit(id, answer)} />
      <div className="x78zum5 x626il7 x6s0dn4">
        <Button tone="quiet" size="small" disabled={saving} onClick={() => {
        moved.current = true;
        if (alreadyComplete) {
          setEditing(false);
          return;
        }
        if (index === 0) navigate("/start");else if (window.history.state === null) setIndex(index - 1);else window.history.back();
      }}>
          Back
        </Button>
      </div>
    </Shell>;
}